import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

// Reminders shown to photographers in the mobile app.
//
// The point of this page is that Kim never needs us to change one. The first note is
// hers ("Vehicle Off - All Windows Down - Top Down"); every note after it is a text
// edit here, not a build. Saving bumps the content version, so an edit made at 10am
// reaches the next car rather than tomorrow morning.
//
// A reminder spends a photographer's attention. Randall, 2026-09-19: "we can over do
// this and make it annoying while they are in robotic memory to shoot and we flip up
// screen prompts that break their flow." Hence the warning past three active notes in
// one placement, and the expiry field: a list nobody prunes is a list nobody reads.

// `live` = the mobile app actually renders this placement today.
//
// ⚠️ DO NOT LET SOMEONE WRITE A NOTE THAT GOES NOWHERE. The table, the API and this page
// all handle three placements, but build 154 only renders the first. A note saved into an
// unrendered placement would look saved, look active, and be seen by nobody — the silent
// success that costs the most here. Flip `live` to true in the same release that wires the
// screen, never before.
const PLACEMENTS = [
  {
    key: 'shoot_start',
    label: 'Before the first car',
    live: true,
    hint: 'Shown once a day, just before the camera opens on their first vehicle. Best for anything about the CAR itself, because this is the only moment they can act on it.',
  },
  {
    key: 'day_start',
    label: 'Start of day',
    live: false,
    hint: 'Will be shown once a day when they open their inventory. Best for shift-wide things such as safety, dress or paperwork.',
  },
  {
    key: 'visit_end',
    label: 'Before leaving a dealership',
    live: false,
    // ⚠️ NOT "end of day" — it was called that and the name was wrong. A photographer does
    // several rooftops in a day and needs this at EACH departure. Renamed before anyone
    // wrote content into it.
    hint: 'Will be shown when they stop shooting at a rooftop, while they are still there. This is the last moment a car that is short on photos can be fixed without a return trip — the close-out itself is what shows them which cars those are.',
  },
];

const BLANK = {
  placement: 'shoot_start',
  title: '',
  body: '',
  is_active: true,
  sort_order: 10,
  expires_at: '',
};

export default function AdminReminders() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(null);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' };
  }, [navigate]);

  const load = useCallback(() => {
    const h = authHeader();
    if (!h) return;
    setLoading(true);
    fetch(API_BASE + '/admin/reminders', { headers: h })
      .then(adminJson)
      .then((d) => setRows(d.reminders || []))
      .catch(() => setError('Failed to load reminders'))
      .finally(() => setLoading(false));
  }, [authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const save = () => {
    const h = authHeader();
    if (!h || !editing) return;
    if (!editing.title.trim() || !editing.body.trim()) {
      setError('Title and message are both required');
      return;
    }
    const isNew = !editing.id;
    fetch(API_BASE + '/admin/reminders' + (isNew ? '' : '/' + editing.id), {
      method: isNew ? 'POST' : 'PUT',
      headers: h,
      body: JSON.stringify({
        placement: editing.placement,
        title: editing.title,
        body: editing.body,
        dealership_id: editing.dealership_id || null,
        is_active: !!editing.is_active,
        sort_order: Number(editing.sort_order) || 10,
        expires_at: editing.expires_at || null,
      }),
    })
      .then(adminJson)
      .then(() => {
        setEditing(null);
        setError('');
        setNotice(
          isNew
            ? 'Added. Photographers see it on their next car.'
            : 'Saved. The change reaches phones on their next car, with no app update.'
        );
        load();
      })
      .catch(() => setError('Save failed'));
  };

  const remove = (row) => {
    const h = authHeader();
    if (!h) return;
    if (!window.confirm('Delete "' + row.title + '"? To keep it for later, switch it off instead.')) return;
    fetch(API_BASE + '/admin/reminders/' + row.id, { method: 'DELETE', headers: h })
      .then(adminJson)
      .then(() => {
        setNotice('Deleted.');
        load();
      })
      .catch(() => setError('Delete failed'));
  };

  const activeIn = (key) => rows.filter((r) => r.placement === key && r.is_active).length;

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;

  return (
    <div style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 4 }}>Photographer Reminders</h1>
      <p style={{ color: '#555', marginTop: 0 }}>
        Notes shown in the mobile app. Changes reach phones immediately, with no app update.
      </p>

      {error ? (
        <div style={{ background: '#fdecea', color: '#b3261e', padding: 10, borderRadius: 6, marginBottom: 12 }}>
          {error}
        </div>
      ) : null}
      {notice ? (
        <div style={{ background: '#e7f5e9', color: '#1b5e20', padding: 10, borderRadius: 6, marginBottom: 12 }}>
          {notice}
        </div>
      ) : null}

      {PLACEMENTS.map((p) => {
        const mine = rows.filter((r) => r.placement === p.key);
        const n = activeIn(p.key);
        return (
          <section key={p.key} style={{ marginBottom: 28, border: '1px solid #e0e0e0', borderRadius: 8, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>
                {p.label}
                {!p.live ? (
                  <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: '#8a5300', background: '#fff4e5', padding: '2px 8px', borderRadius: 10 }}>
                    not showing yet
                  </span>
                ) : null}
              </h2>
              <button
                disabled={!p.live}
                title={p.live ? '' : 'The app does not show this moment yet'}
                onClick={() => setEditing(Object.assign({}, BLANK, { placement: p.key }))}
              >
                + Add
              </button>
            </div>
            <p style={{ color: '#666', fontSize: 13, marginTop: 6 }}>{p.hint}</p>

            {!p.live ? (
              <div style={{ background: '#fff4e5', color: '#8a5300', padding: 10, borderRadius: 6, fontSize: 13, marginBottom: 10 }}>
                The app does not show this moment yet, so anything saved here would be seen by
                nobody. It is switched off on purpose rather than hidden, so you can see what is
                coming. Ask and it goes into the next build.
              </div>
            ) : null}

            {n > 3 ? (
              <div style={{ background: '#fff4e5', color: '#8a5300', padding: 10, borderRadius: 6, fontSize: 13, marginBottom: 10 }}>
                {n} active notes here. They appear together on one screen. Past three, people stop
                reading all of them, so consider setting an expiry on the ones that were only for a
                particular week.
              </div>
            ) : null}

            {mine.length === 0 ? (
              <p style={{ color: '#999', fontSize: 13 }}>
                Nothing here. Photographers see no prompt at this moment.
              </p>
            ) : null}

            {mine.map((r) => (
              <div key={r.id} style={{ borderTop: '1px solid #eee', padding: '10px 0', display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <strong>{r.title}</strong>
                  {!r.is_active ? <span style={{ color: '#999', fontSize: 12 }}> (off)</span> : null}
                  {r.expires_at ? (
                    <span style={{ color: '#8a5300', fontSize: 12 }}> · expires {String(r.expires_at).slice(0, 10)}</span>
                  ) : null}
                  {r.dealership_name ? (
                    <span style={{ color: '#555', fontSize: 12 }}> · {r.dealership_name} only</span>
                  ) : null}
                  <div style={{ color: '#333', whiteSpace: 'pre-wrap' }}>{r.body}</div>
                  <div style={{ color: '#999', fontSize: 11 }}>v{r.version}</div>
                </div>
                <div>
                  <button
                    onClick={() =>
                      setEditing(
                        Object.assign({}, r, {
                          expires_at: r.expires_at ? String(r.expires_at).slice(0, 10) : '',
                        })
                      )
                    }
                  >
                    Edit
                  </button>{' '}
                  <button onClick={() => remove(r)}>Delete</button>
                </div>
              </div>
            ))}
          </section>
        );
      })}

      {editing ? (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, width: 560, maxWidth: '92%' }}>
            <h3 style={{ marginTop: 0 }}>{editing.id ? 'Edit reminder' : 'New reminder'}</h3>

            <label style={{ display: 'block', fontSize: 13, marginTop: 10 }}>When</label>
            <select
              value={editing.placement}
              onChange={(e) => setEditing(Object.assign({}, editing, { placement: e.target.value }))}
              style={{ width: '100%', padding: 6 }}
            >
              {PLACEMENTS.map((p) => (
                <option key={p.key} value={p.key} disabled={!p.live}>
                  {p.label}
                  {p.live ? '' : ' — not showing yet'}
                </option>
              ))}
            </select>

            <label style={{ display: 'block', fontSize: 13, marginTop: 10 }}>Title</label>
            <input
              value={editing.title}
              onChange={(e) => setEditing(Object.assign({}, editing, { title: e.target.value }))}
              placeholder="Before you shoot"
              style={{ width: '100%', padding: 6 }}
            />

            <label style={{ display: 'block', fontSize: 13, marginTop: 10 }}>Message</label>
            <textarea
              value={editing.body}
              onChange={(e) => setEditing(Object.assign({}, editing, { body: e.target.value }))}
              rows={3}
              placeholder="Vehicle Off - All Windows Down - Top Down (if Convertible)"
              style={{ width: '100%', padding: 6 }}
            />

            <label style={{ display: 'block', fontSize: 13, marginTop: 10 }}>Stop showing after (optional)</label>
            <input
              type="date"
              value={editing.expires_at || ''}
              onChange={(e) => setEditing(Object.assign({}, editing, { expires_at: e.target.value }))}
              style={{ padding: 6 }}
            />
            <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
              Use this for anything tied to a week or a month. It disappears on its own.
            </div>

            <label style={{ display: 'block', fontSize: 13, marginTop: 12 }}>
              <input
                type="checkbox"
                checked={!!editing.is_active}
                onChange={(e) => setEditing(Object.assign({}, editing, { is_active: e.target.checked }))}
              />{' '}
              Active
            </label>

            <div style={{ marginTop: 18, textAlign: 'right' }}>
              <button
                onClick={() => {
                  setEditing(null);
                  setError('');
                }}
              >
                Cancel
              </button>{' '}
              <button onClick={save} style={{ fontWeight: 600 }}>
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
